import React, { useState, useCallback, useMemo } from 'react';
import type { FC } from 'react';
import type { VariantOf } from 'variant';
import { match, variant } from 'variant';
import { kebabCase } from 'lodash';
import {
  Link,
  SelectGroupsInput,
  SelectInput,
  Spinner,
  Text,
  TextInput,
  useAsync,
} from '@wix/cli-ui-kit';
import type { SelectGroup } from '@wix/cli-ui-kit';
import type { BackOfficeExtensionContainer } from '@wix/extensions-data-client';
import type { ActionSchema } from '@wix/cli-app-definitions';
import { useDevCenterClient } from '@wix/dev-center-client';
import {
  useValidation,
  createDashboardMenuPluginValidation,
} from '../../../../dev-center/components/validations/index.js';
import { generateDashboardMenuPlugin } from './generate.js';
import { useGenerationTask } from '../../hooks/use-generation-task.js';
import { useProjectModel } from '../../../../model.js';
import { NavigateToPage } from './NavigateToPage.js';
import { OpenModal } from './OpenModal.js';
import { useWixCliAstroBi } from '../../../../bi/index.js';
import { InstallDependencies } from '../../InstallDependencies.js';
import { MultipleAppRequirement } from '../../MultipleAppRequirement.js';
import { DashboardMenuPluginGeneratedNote } from './DashboardMenuPluginGeneratedNote.js';
import type { SlotsApp } from '../DashboardPlugin.js';

const DashboardMenuPluginAction = variant({
  NavigateToPage: {},
  OpenModal: {},
});

type DashboardMenuPluginAction = VariantOf<typeof DashboardMenuPluginAction>;

const docsLink = (externalId?: string | null) =>
  new URL(
    `?portalSlug=sdk&externalId=${externalId}&fallbackUri=/build-apps/develop-your-app/extensions/dashboard-extensions/dashboard-plugins/dashboard-menu-plugins/dashboard-menu-plugin-extensions&platform=CLI`,
    'https://dev.wix.com/_api/docs-redirect/by-external-id'
  ).href;

export const DashboardMenuPluginWizard: FC<{
  pluginsMap: Record<string, BackOfficeExtensionContainer>;
  slotsApp: SlotsApp;
}> = ({ slotsApp, pluginsMap }) => {
  const bi = useWixCliAstroBi();
  const { model } = useProjectModel();
  const { addRequiredApp } = useDevCenterClient();

  const [backofficeMenuItem, setBackofficeMenuItem] =
    useState<BackOfficeExtensionContainer>();
  const [actionType, setActionType] = useState<DashboardMenuPluginAction>();
  const [requireAppsIds, setRequireAppsIds] = useState<string[]>();
  const [action, setAction] = useState<ActionSchema>();
  const [folder, setFolder] = useState<string>();
  const [title, setTitle] = useState<string>();
  const [finishedInstallation, setFinishedInstallation] =
    useState<boolean>(false);

  const defaultFolderName = kebabCase(title);

  const [additionalInfo, setAdditionalInfo] =
    useState<Record<string, unknown>>();

  const validateMenuPluginFolder = useValidation(
    createDashboardMenuPluginValidation(model.projectFolder)
  );

  const generationTask = useGenerationTask(async () => {
    if (backofficeMenuItem && action && folder && title && requireAppsIds) {
      const requireAppId = requireAppsIds.at(0);

      if (requireAppId) {
        await addRequiredApp({
          appId: model.config.appId,
          appIdToRequire: requireAppId,
        });
      }

      return generateDashboardMenuPlugin(
        model.projectFolder,
        backofficeMenuItem.component.componentId,
        action,
        folder,
        title
      );
    }
  }, [backofficeMenuItem, action, folder, title]);

  const handleActionSelected = useCallback(
    (action: ActionSchema) => setAction(action),
    []
  );

  const { status } = useAsync(async () => {
    const backofficeExtensionWidgets = Object.values(
      slotsApp.backofficePages.reduce<
        Record<string, SelectGroup<BackOfficeExtensionContainer>>
      >((extensionsByPages, backofficePage) => {
        extensionsByPages[backofficePage.component.componentId] ??= {
          title:
            backofficePage.component.data.backOfficePage.metaData?.title ??
            backofficePage.component.name,
          items: [],
        };

        for (const slotId of backofficePage.component.data.backOfficePage
          .slotIds) {
          const backofficeMenuItem = pluginsMap[slotId];
          if (backofficeMenuItem) {
            extensionsByPages[backofficePage.component.componentId]?.items.push(
              {
                title:
                  backofficeMenuItem.component.data.backOfficeExtensionContainer
                    .slotData?.displayName ?? backofficeMenuItem.component.name,
                description: backofficeMenuItem.component.externalId ? (
                  <Text>
                    <Link
                      url={docsLink(backofficeMenuItem.component.externalId)}
                    >
                      read more
                    </Link>
                  </Text>
                ) : null,
                key: backofficeMenuItem.component.componentId,
                value: backofficeMenuItem,
              }
            );
          }
        }

        return extensionsByPages;
      }, {})
    );

    for (const backofficeExtensionWidget of backofficeExtensionWidgets) {
      backofficeExtensionWidget.items.sort((a, b) =>
        a.title.localeCompare(b.title)
      );
    }

    backofficeExtensionWidgets.sort((a, b) => a.title.localeCompare(b.title));

    return backofficeExtensionWidgets;
  }, []);

  const task = useMemo(
    () =>
      match(generationTask, {
        Success: ({ hasUpdatedDependencies, newFiles }) => {
          if (!hasUpdatedDependencies) {
            setFinishedInstallation(true);
          }
          return (
            <>
              {hasUpdatedDependencies && (
                <InstallDependencies
                  onFinish={() => setFinishedInstallation(true)}
                />
              )}
              {finishedInstallation && backofficeMenuItem && (
                <DashboardMenuPluginGeneratedNote
                  backofficeMenuItem={backofficeMenuItem}
                  generatedFiles={newFiles}
                />
              )}
            </>
          );
        },
        Loading: () => <Spinner text={`Generating dashboard menu plugin...`} />,
        Error: () => null,
        NotRequested: () => null,
      }),
    [
      generationTask,
      setFinishedInstallation,
      finishedInstallation,
      backofficeMenuItem,
    ]
  );

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text={'Loading...'} />,
    Success: ({ result: menuItemsGroups }) => (
      <>
        <SelectGroupsInput<BackOfficeExtensionContainer>
          label={'What would you like to extend?'}
          hideScroll
          info={
            <Text>
              Menu Plugins let you extend Wix apps by adding a menu item in a
              pre-defined slot in a menu on a dashboard page (
              <Link url="https://wix.to/h8AJ7f5">read more</Link>)
            </Text>
          }
          groups={menuItemsGroups}
          onSubmit={(item) => {
            const additionalInfoOfSelectedItem = {
              category: slotsApp.appName,
              extensionName: item.value.component.name,
              extensionType: 'dashboard menu plugin',
            };

            bi.cliFlowStepAnswered({
              question: 'What would you like to extend?',
              questionKey:
                'generate_command.dashboard_menu_plugin.what_to_extend',
              answer: item.title,
              additionalInfo: additionalInfoOfSelectedItem,
            });

            setAdditionalInfo(additionalInfoOfSelectedItem);
            setBackofficeMenuItem(item.value);
          }}
        />
        {backofficeMenuItem && (
          <MultipleAppRequirement
            appId={model.config.appId}
            requiredApps={slotsApp.requiredApps.map((requiredApp) => ({
              id: requiredApp.appId,
              name: requiredApp.appName,
            }))}
            onSubmit={setRequireAppsIds}
          />
        )}
        {requireAppsIds && (
          <SelectInput<DashboardMenuPluginAction>
            label={'What action will happen after clicking on the menu item?'}
            options={[
              {
                key: 'navigate-to-page',
                title: 'Navigate to a page',
                value: DashboardMenuPluginAction.NavigateToPage(),
              },
              {
                key: 'open-modal',
                title: 'Open a modal',
                value: DashboardMenuPluginAction.OpenModal(),
              },
            ]}
            onSubmit={(action) => {
              bi.cliFlowStepAnswered({
                question:
                  'What action will happen after clicking on the menu item?',
                questionKey:
                  'generate_command.dashboard_menu_plugin.what_to_do',
                answer: action.title,
                additionalInfo,
              });
              setActionType(action.value);
            }}
          />
        )}
        {actionType &&
          match(actionType, {
            NavigateToPage: () => (
              <NavigateToPage onActionSelected={handleActionSelected} />
            ),
            OpenModal: () => (
              <OpenModal onActionSelected={handleActionSelected} />
            ),
          })}
        {action && (
          <TextInput
            label={'Menu Plugin title'}
            placeholder={'My Plugin'}
            onSubmit={(value) => {
              bi.cliFlowStepAnswered({
                question: 'Menu Plugin title',
                questionKey:
                  'generate_command.dashboard_menu_plugin.choose_title',
                answer: value,
                additionalInfo,
              });
              setTitle(value);
            }}
          />
        )}
        {title && (
          <TextInput
            label={'Menu Plugin folder'}
            placeholder={defaultFolderName}
            onSubmit={(folder) => {
              bi.cliFlowStepAnswered({
                question: 'Menu Plugin folder',
                questionKey:
                  'generate_command.dashboard_menu_plugin.choose_folder',
                answer: folder,
                additionalInfo,
              });
              setFolder(folder);
            }}
            validate={validateMenuPluginFolder}
          />
        )}
        {task}
      </>
    ),
  });
};
